// src/components/loyalty/TransferRegrets.jsx
import React, { useState } from 'react';
import { ChevronLeft, ChevronRight, Heart, X } from 'lucide-react';
import { useManagerContext } from '../../context/DataContext';
import { useSwipeCard } from '../../hooks/useSwipeCard';
import { 
  PageContainer, 
  Card, 
  NavigationButtons 
} from '../shared/Navigation';

const TransferRegrets = () => {
  const [currentCardIndex, setCurrentCardIndex] = useState(0);
  
  const { getManagerData } = useManagerContext();
  const managerData = getManagerData();
  
  // Extract loyalty issues data
  const { summary: loyaltySummary, details: loyaltyIssues = [] } = managerData?.loyaltyIssues || {};
  
  // Get the total points missed from the summary data
  const totalPointsMissed = loyaltySummary?.Total_Points_Missed || 0;
  const loyaltyIssuesCount = loyaltySummary?.Loyalty_Issues_Count || loyaltyIssues.length;
  
  // Navigation functions - removed shareResults function
  
  // Swipe handlers
  const nextCard = () => {
    if (currentCardIndex < loyaltyIssues.length - 1) {
      setCurrentCardIndex(currentCardIndex + 1);
    }
  };

  const prevCard = () => {
    if (currentCardIndex > 0) {
      setCurrentCardIndex(currentCardIndex - 1);
    }
  };
  
  // Use the custom hook for swipe handling
  const {
    offsetX,
    animation,
    handlers,
    triggerSwipeLeft,
    triggerSwipeRight
  } = useSwipeCard(nextCard, prevCard);
  
  // If we have no loyalty issues, show a different message
  if (loyaltyIssues.length === 0) {
    return (
      <PageContainer 
        title="Transfer Regrets 😬"
        titleColor="text-pink-300"
        gradientFrom="from-pink-900"
        gradientTo="to-indigo-900"
      >
        <Card className="text-center">
          <div className="w-20 h-20 bg-green-500 rounded-full mx-auto mb-6 flex items-center justify-center">
            <span className="text-4xl">🎯</span>
          </div>
          
          <h2 className="text-2xl font-bold mb-4">Transfer Master!</h2>
          <p className="text-lg mb-8">
            Wow! You had no loyalty issues this season. 
            You never sold a player right before they hauled. 
            That's impressive transfer timing!
          </p>
          
          <NavigationButtons 
            backPath="/journey-selector"
            forwardPath="/my-journey"
            backText="Back to Journey Selector"
            forwardText="Go to My Journey"
          />
        </Card>
      </PageContainer>
    );
  }

  // Current card data
  const currentCard = loyaltyIssues[currentCardIndex] || {};
  
  // Format performance details for display
  const formatPerformance = (card) => {
    const details = [];
    
    if (card.goals > 0) {
      details.push(`${card.goals} goal${card.goals > 1 ? 's' : ''}`);
    }
    
    if (card.assists > 0) {
      details.push(`${card.assists} assist${card.assists > 1 ? 's' : ''}`);
    }
    
    if (card.bonus > 0) {
      details.push(`${card.bonus} bonus point${card.bonus > 1 ? 's' : ''}`);
    }
    
    if (card.clean_sheet) {
      details.push('clean sheet');
    }
    
    if (details.length === 0) {
      return '';
    }
    
    return details.join(', ');
  };

  return (
    <PageContainer 
      title="Transfer Regrets 😬"
      titleColor="text-pink-300"
      gradientFrom="from-pink-900"
      gradientTo="to-indigo-900"
    >
      <div 
        className={`relative w-full max-w-sm h-96 bg-white rounded-2xl shadow-xl overflow-hidden`}
        style={{ 
          transform: animation === 'swipe-left' 
            ? 'translateX(-150%) rotate(-20deg)' 
            : animation === 'swipe-right' 
              ? 'translateX(150%) rotate(20deg)' 
              : `translateX(${offsetX}px)`,
          transition: offsetX !== 0 ? 'none' : 'transform 0.3s ease-out'
        }}
        {...handlers}
      >
        <div className="absolute inset-0 p-6 flex flex-col items-center text-gray-800">
          <div className="w-24 h-24 rounded-full overflow-hidden mb-4 border-4 border-pink-200 bg-pink-100 flex items-center justify-center">
            <span className="text-4xl">⚽️</span>
          </div>
          
          <h3 className="text-xl font-bold mb-1">{currentCard.player}</h3>
          
          <div className="text-center mt-4">
            <p className="text-lg">
              You sold <span className="font-bold">{currentCard.player}</span> right before a <span className="font-bold text-pink-600">{currentCard.points_missed}-point</span> haul in GW{currentCard.gw_haul}!
            </p>
            
            {formatPerformance(currentCard) && (
              <p className="text-gray-500 mt-2">
                They returned with {formatPerformance(currentCard)}.
              </p>
            )}
            
            <p className="text-gray-500 mt-6 italic">
              That's like giving points away to your rivals...
            </p>
          </div>
          
          <div className="absolute bottom-6 flex items-center justify-between w-full px-8">
            <button 
              onClick={prevCard}
              disabled={currentCardIndex === 0}
              className={`rounded-full w-12 h-12 flex items-center justify-center ${currentCardIndex === 0 ? 'text-gray-300' : 'text-pink-500 hover:bg-pink-50'}`}
            >
              <ChevronLeft size={24} />
            </button>
            
            <div className="text-gray-400 text-sm">
              {currentCardIndex + 1}/{loyaltyIssues.length}
            </div>
            
            <button 
              onClick={nextCard}
              disabled={currentCardIndex === loyaltyIssues.length - 1}
              className={`rounded-full w-12 h-12 flex items-center justify-center ${currentCardIndex === loyaltyIssues.length - 1 ? 'text-gray-300' : 'text-pink-500 hover:bg-pink-50'}`}
            >
              <ChevronRight size={24} />
            </button>
          </div>
        </div>
        
        <div className="absolute top-4 right-4 flex space-x-2">
          <button 
            onClick={triggerSwipeRight}
            className="bg-green-50 text-green-600 p-2 rounded-full hover:bg-green-100"
          >
            <Heart size={20} />
          </button>
          <button 
            onClick={triggerSwipeLeft}
            className="bg-red-50 text-red-600 p-2 rounded-full hover:bg-red-100"
          >
            <X size={20} />
          </button>
        </div>
      </div>
      
      <div className="text-center mt-6 w-full max-w-md">
        <p className="text-lg font-semibold">
          Your loyalty issues cost you {totalPointsMissed} points!
        </p>
        <p className="text-gray-300 mb-4">
          {loyaltyIssuesCount > 3 
            ? "That's among the worst in your league! 😬"
            : "Try to watch your transfer timing better next season."}
        </p>
        
        <NavigationButtons 
          backPath="/journey-selector"
          forwardPath="/my-journey"
          backText="Back to Journey Selector"
          forwardText="Go to My Journey"
        />
      </div>
    </PageContainer>
  );
};

export default TransferRegrets;
